import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Clock,
  ArrowRight,
  Sparkles,
  Cpu,
  Thermometer,
  Boxes,
  MoveRight,
  ExternalLink,
  Cable,
  Search,
  X,
  Filter
} from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import {
  SCENARIOS_REGISTRY,
  ScenarioStatus,
  ScenarioCategory,
  getScenarioStatusBadgeClass,
  getScenarioCounts,
  filterScenarios,
  getScenarioCategoryLabel,
} from '../utils/scenarios.logic';
import { useDcStore } from '../stores/dcStore';
import { resolvePageDcCode } from '../utils/dcContext.logic';

export default function ScenariosPage() {
  const { dcCode } = useParams<{ dcCode?: string }>();
  const { activeDcCode, datacenters } = useDcStore();
  const actualDc = resolvePageDcCode(dcCode, activeDcCode, datacenters[0]?.code) || '';

  const [statusFilter, setStatusFilter] = useState<ScenarioStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<ScenarioCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const counts = useMemo(() => getScenarioCounts(SCENARIOS_REGISTRY), []);

  const filteredScenarios = useMemo(() => {
    return filterScenarios(SCENARIOS_REGISTRY, {
      status: statusFilter,
      category: categoryFilter,
      query: searchQuery,
    });
  }, [statusFilter, categoryFilter, searchQuery]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'impact':
        return <Cpu className="w-4 h-4 text-cyan-400" />;
      case 'thermal':
        return <Thermometer className="w-4 h-4 text-rose-400" />;
      case 'capacity':
        return <Boxes className="w-4 h-4 text-amber-400" />;
      case 'migration':
        return <MoveRight className="w-4 h-4 text-purple-400" />;
      case 'connectivity':
        return <Cable className="w-4 h-4 text-emerald-400" />;
      default:
        return <Sparkles className="w-4 h-4 text-cyan-400" />;
    }
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setCategoryFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = statusFilter !== 'all' || categoryFilter !== 'all' || searchQuery.trim().length > 0;

  return (
    <div className="p-6 animate-fade-in text-xs font-mono space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold font-sans text-slate-100 mb-1">Сценарии автоматизации</h1>
        <p className="text-xs text-slate-400 font-sans">
          Каталог сценариев моделирования, анализа воздействия и прогнозирования ресурсов ЦОД
        </p>
      </div>

      {/* Prominent WIP Banner */}
      <div
        data-testid="scenarios-wip-banner"
        className="bg-gradient-to-r from-amber-950/40 via-slate-900 to-cyan-950/30 border border-amber-500/40 rounded-xl p-5 shadow-lg relative overflow-hidden"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 shrink-0">
            <Sparkles className="w-6 h-6 animate-pulse" />
          </div>
          <div className="space-y-1.5 flex-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 uppercase tracking-wider font-mono">
                В разработке // Coming Soon
              </span>
              <span className="text-xs text-slate-400 font-mono">
                Сценарный движок: What-If &amp; Capacity Planning
              </span>
            </div>
            <h2 className="text-base font-bold text-slate-100 font-sans">
              Централизованный интерактивный центр сценариев
            </h2>
            <p className="text-xs text-slate-300 leading-relaxed font-sans">
              Полноразмерный конструктор пакетных сценариев (What-If симуляции, оркестрация миграции оборудования и прогнозирование емкости стоек по U-юнитам и электропитанию NEC 80%) находится в стадии разработки.
              Ниже приведены сценарии, доступные в системе уже сейчас через контекстные панели ЦОД, а также запланированные модули.
            </p>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4 space-y-3 font-sans">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по сценариям..."
              className="w-full pl-9 pr-8 py-2 bg-slate-900 border border-slate-800 rounded-lg text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
              data-testid="scenarios-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-200"
                title="Очистить"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 flex-wrap w-full md:w-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded text-xs font-bold transition ${
                statusFilter === 'all'
                  ? 'bg-cyan-950/80 text-cyan-300 border border-cyan-700/80 shadow'
                  : 'bg-slate-900/60 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800'
              }`}
            >
              Все ({counts.total})
            </button>
            <button
              onClick={() => setStatusFilter('available')}
              className={`px-3 py-1.5 rounded text-xs font-bold transition flex items-center gap-1 ${
                statusFilter === 'available'
                  ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-700/80 shadow'
                  : 'bg-slate-900/60 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800'
              }`}
            >
              Доступные ({counts.available})
            </button>
            <button
              onClick={() => setStatusFilter('in_development')}
              className={`px-3 py-1.5 rounded text-xs font-bold transition flex items-center gap-1 ${
                statusFilter === 'in_development'
                  ? 'bg-amber-950/80 text-amber-300 border border-amber-700/80 shadow'
                  : 'bg-slate-900/60 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800'
              }`}
            >
              В разработке ({counts.in_development})
            </button>
            <button
              onClick={() => setStatusFilter('planned')}
              className={`px-3 py-1.5 rounded text-xs font-bold transition flex items-center gap-1 ${
                statusFilter === 'planned'
                  ? 'bg-slate-800 text-slate-200 border border-slate-600 shadow'
                  : 'bg-slate-900/60 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800'
              }`}
            >
              Запланированные ({counts.planned})
            </button>
          </div>
        </div>

        {/* Category Pills & Reset Button */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-850 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-slate-500 font-mono mr-1 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Категория:
            </span>
            {(['all', 'impact', 'thermal', 'capacity', 'connectivity', 'migration'] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-2.5 py-1 rounded text-[11px] transition ${
                  categoryFilter === cat
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 font-bold'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/60 border border-transparent'
                }`}
              >
                {cat === 'all' ? 'Все' : getScenarioCategoryLabel(cat)}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              className="text-xs text-slate-400 hover:text-rose-400 transition underline underline-offset-2"
            >
              Сбросить фильтры
            </button>
          )}
        </div>
      </div>

      {/* Scenarios Catalog Grid */}
      {filteredScenarios.length === 0 ? (
        <div className="p-8 text-center bg-slate-950/40 border border-slate-800 rounded-xl space-y-3 font-sans">
          <p className="text-slate-400 text-sm">Сценариев по заданным критериям не найдено.</p>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-cyan-300 rounded font-bold transition text-xs"
          >
            Сбросить все фильтры
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredScenarios.map((scenario) => {
            const badge = getScenarioStatusBadgeClass(scenario.status);

            return (
              <Card key={scenario.id} className="flex flex-col justify-between border-slate-800/80 hover:border-slate-700 transition">
                <div>
                  <CardHeader
                    title={scenario.title}
                    actions={
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded border uppercase tracking-wider ${badge.bg} ${badge.text} ${badge.border}`}>
                        ● {scenario.statusLabel}
                      </span>
                    }
                  />
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-2 text-slate-400 font-sans">
                      {getCategoryIcon(scenario.category)}
                      <span className="text-xs uppercase tracking-wider font-mono text-slate-400">
                        Категория: {scenario.categoryLabel || scenario.category}
                      </span>
                    </div>
                    <p className="text-slate-300 leading-relaxed font-sans text-xs">
                      {scenario.description}
                    </p>
                    {scenario.howToUse && (
                      <div className="bg-slate-950/60 border border-slate-800/80 rounded-lg p-3 text-slate-400 font-sans text-xs">
                        <span className="font-bold text-slate-200 block mb-1 font-mono">Инструкция по запуску:</span>
                        {scenario.howToUse}
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-5 pt-0">
                  {scenario.linkPath ? (
                    <Link
                      to={`/${actualDc}${scenario.linkPath}`}
                      className="flex items-center justify-center gap-2 w-full py-2 bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-cyan-800/50 rounded font-sans font-bold transition text-xs"
                      aria-label={`${scenario.linkLabel || 'Открыть модуль'}: ${scenario.title}`}
                    >
                      {scenario.linkLabel || 'Открыть модуль'} <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  ) : (
                    <div className="text-center py-2 text-[11px] font-mono text-slate-500 border border-slate-850 rounded bg-slate-950/40">
                      Ожидается в следующих релизах
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Work Orders Integration */}
      <Card>
        <CardHeader title="Наряды на работы (Work Orders)" />
        <div className="p-5 space-y-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-300 font-sans leading-relaxed text-xs">
            Все сгенерированные шаги сценариев (демонтаж, монтаж, перекоммутация портов) автоматически транслируются в задачи для дежурных инженеров ЦОД.
          </p>
          <Link
            to={`/${actualDc}/tasks`}
            className="shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 bg-cyan-950/50 hover:bg-cyan-900/60 border border-cyan-800/60 text-cyan-300 rounded-lg font-sans font-bold transition shadow-lg shadow-cyan-950/40 text-xs"
          >
            <Clock className="w-4 h-4" /> Перейти к Нарядам и Задачам <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Card>
    </div>
  );
}

